import { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe, formatPrice } from '@/lib/stripe';
import { CheckoutForm } from './CheckoutForm';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string; // server-side price lookup key
  amount: number; // in cents — display only, server owns the authoritative price
  productName: string;
  productDescription?: string;
  onSuccess: () => void;
}

export function PaymentModal({
  open,
  onOpenChange,
  productId,
  amount,
  productName,
  productDescription,
  onSuccess,
}: PaymentModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthError, setIsAuthError] = useState(false);

  useEffect(() => {
    if (open && !clientSecret) {
      initializePayment();
    }
  }, [open]);

  const initializePayment = async () => {
    setLoading(true);
    setError(null);
    setIsAuthError(false);

    try {
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');

      const result = await createPaymentIntent({ productId });

      const data = result.data as { clientSecret: string };
      setClientSecret(data.clientSecret);
    } catch (err: unknown) {
      console.error('Failed to create payment intent:', err);
      const e = err as { code?: string; message?: string };
      const code = e.code || '';
      let message: string;
      if (code === 'functions/internal' || e.message === 'internal') {
        message = 'Our payment service is temporarily unavailable. Please try again later.';
      } else if (code === 'functions/unavailable') {
        message = 'Payment service is currently unavailable. Please try again later.';
      } else if (code === 'functions/unauthenticated') {
        message = 'Please log in to make a purchase.';
        setIsAuthError(true);
      } else {
        message = 'Failed to initialize payment. Please try again.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    onSuccess();
    onOpenChange(false);
    // Reset state for next time
    setClientSecret(null);
    setError(null);
    setIsAuthError(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset state
    setClientSecret(null);
    setError(null);
    setIsAuthError(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Your Purchase</DialogTitle>
        </DialogHeader>

        <div className="space-y-1 -mt-2">
          <div className="font-medium text-foreground">{productName}</div>
          {productDescription && (
            <div className="text-sm text-muted-foreground">{productDescription}</div>
          )}
          <div className="text-lg font-semibold text-foreground">
            {formatPrice(amount, 'usd')}
          </div>
        </div>

        <div className="mt-4">
          {error && (
            <Alert variant={isAuthError ? "default" : "destructive"} className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {isAuthError ? (
                  <>Please <Link to="/login" className="underline font-medium text-primary">log in</Link> to make a purchase.</>
                ) : error}
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : clientSecret && getStripe() ? (
            <Elements
              stripe={getStripe()!}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#84a98c', // Sage green from your theme
                    colorBackground: '#ffffff',
                    colorText: '#1a1a1a',
                    colorDanger: '#dc2626',
                    fontFamily: 'system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              <CheckoutForm onSuccess={handleSuccess} onCancel={handleCancel} />
            </Elements>
          ) : !error ? (
            <div className="text-center py-8 text-muted-foreground">
              Failed to initialize payment. Please try again.
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}